<template>
  <hs-modal
    model-value
    title="Profile settings"
    kicker="Account"
    description="Keep your dashboard identity aligned with your mobile HovaStore profile."
    size="lg"
    @close="closeModal"
    @update:modelValue="handleModelValueChange"
  >
    <div class="profile-settings">
      <section class="profile-settings__summary">
        <div class="profile-settings__avatar">
          {{ userInitials }}
        </div>

        <div class="profile-settings__identity">
          <h3>{{ user?.names || "HovaStore user" }}</h3>
          <p>{{ user?.phone_number || "No phone number" }}</p>
          <div class="profile-settings__badges">
            <hs-badge v-if="hasAdminPermissions" tone="primary" icon="shield">
              Admin
            </hs-badge>
            <hs-badge v-if="isSuperAdmin" tone="warning" icon="award">
              Super Admin
            </hs-badge>
            <hs-badge v-if="selectedStoreEbmActivated" tone="success" icon="check-circle">
              EBM Activated
            </hs-badge>
          </div>
        </div>
      </section>

      <form class="profile-settings__form" @submit.prevent="saveProfile">
        <div class="profile-settings__grid">
          <hs-input
            v-model="form.names"
            label="Full name"
            placeholder="Your full name"
            icon="user"
            required
            :error="errors.names"
            autocomplete="name"
          />

          <hs-select
            v-model="form.gender"
            label="Gender"
            placeholder="Select gender"
            icon="users"
            required
            :options="genderOptions"
            :error="errors.gender"
          />

          <hs-input
            v-model="form.email"
            label="Email"
            placeholder="name@example.com"
            icon="mail"
            :error="errors.email"
            autocomplete="email"
          />

          <hs-input
            :model-value="user?.phone_number || ''"
            label="Phone number"
            icon="phone"
            disabled
            hint="Phone number changes are handled through account verification."
          />
        </div>

        <div class="profile-settings__notice">
          <hs-icon name="info" :size="17" />
          <span>
            This matches the mobile profile flow: name and gender are required,
            while email is optional but must be valid when provided.
          </span>
        </div>
      </form>
    </div>

    <template #footer>
      <hs-button variant="secondary" @click="closeModal">Cancel</hs-button>
      <hs-button
        icon="save"
        :loading="isSaving"
        :disabled="isSaving || !canSave"
        @click="saveProfile"
      >
        Save profile
      </hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { toast } from "vue3-toastify";
import { mapGetters, mapMutations } from "vuex";
import http from "@/helper/http";
import api from "@/helper/api";
import {
  HsBadge,
  HsButton,
  HsIcon,
  HsInput,
  HsModal,
  HsSelect,
} from "@/components/ui";

const GENDER_OPTIONS = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Non binary", value: "NON_BINARY" },
];

export default {
  name: "UpdateProfile",
  components: {
    HsBadge,
    HsButton,
    HsIcon,
    HsInput,
    HsModal,
    HsSelect,
  },
  props: {
    showProfileModal: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      form: {
        names: "",
        gender: "",
        email: "",
      },
      errors: {
        names: "",
        gender: "",
        email: "",
      },
      isSaving: false,
      genderOptions: GENDER_OPTIONS,
    };
  },
  computed: {
    ...mapGetters([
      "rraEbmSettings",
      "hasAdminPermissions",
      "getAuthenticatedUser",
    ]),
    user() {
      return this.getAuthenticatedUser;
    },
    userInitials() {
      const names = String(this.user?.names || "H").trim();
      const parts = names.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return names.charAt(0).toUpperCase() || "H";
    },
    isSuperAdmin() {
      const permissions = this.user?.admin_permissions;
      return Array.isArray(permissions) && permissions.includes("SUPER_ADMIN");
    },
    selectedStoreEbmActivated() {
      return this.rraEbmSettings?.ebm_activated || false;
    },
    canSave() {
      return this.form.names.trim().length > 0 && this.form.gender;
    },
  },
  watch: {
    user: {
      handler() {
        this.syncFormFromUser();
      },
      immediate: true,
    },
  },
  methods: {
    ...mapMutations(["SET_AUTH_USER"]),
    normalizeGender(value) {
      return String(value || "").trim().replace(/\s+/g, "_").toUpperCase();
    },
    syncFormFromUser() {
      this.form = {
        names: this.user?.names || "",
        gender: this.normalizeGender(this.user?.gender),
        email: this.user?.email || "",
      };
      this.errors = {
        names: "",
        gender: "",
        email: "",
      };
    },
    handleModelValueChange(open) {
      if (!open) {
        this.closeModal();
      }
    },
    closeModal() {
      this.$emit("close-modal");
    },
    validateForm() {
      const nextErrors = {
        names: "",
        gender: "",
        email: "",
      };

      if (!this.form.names.trim()) {
        nextErrors.names = "Full name is required";
      }

      if (!this.form.gender) {
        nextErrors.gender = "Gender is required";
      }

      const email = this.form.email.trim();
      if (email && !/^[\w.-]+@([\w-]+\.)+[\w-]{2,}$/.test(email)) {
        nextErrors.email = "Enter a valid email address";
      }

      this.errors = nextErrors;
      return !Object.values(nextErrors).some(Boolean);
    },
    extractErrorMessage(error) {
      return (
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to update profile"
      );
    },
    async refreshAuthenticatedUser() {
      const response = await http.get(api.authenticated_user);
      if (response?.data) {
        this.SET_AUTH_USER(response.data);
      }
    },
    async saveProfile() {
      if (this.isSaving || !this.validateForm()) {
        return;
      }

      this.isSaving = true;
      const payload = {
        names: this.form.names.trim(),
        gender: this.normalizeGender(this.form.gender),
        email: this.form.email.trim(),
      };

      try {
        await http.patch(api.update_user, payload);
        await this.refreshAuthenticatedUser();
        toast.success("Profile updated successfully");
        this.closeModal();
      } catch (error) {
        toast.error(this.extractErrorMessage(error));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-settings {
  display: grid;
  gap: 1rem;
}

.profile-settings__summary {
  align-items: center;
  background:
    radial-gradient(circle at 92% 8%, color-mix(in srgb, var(--hs-primary, #3293ec) 14%, transparent), transparent 34%),
    color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 70%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 24px;
  display: flex;
  gap: 1rem;
  padding: 1rem;
}

.profile-settings__avatar {
  align-items: center;
  background:
    radial-gradient(circle at 82% 0%, rgba(255, 255, 255, 0.38), transparent 35%),
    linear-gradient(135deg, var(--hs-primary, #3293ec), var(--hs-primary-strong, #1d4ed8));
  border-radius: 22px;
  box-shadow: 0 18px 38px color-mix(in srgb, var(--hs-primary, #3293ec) 26%, transparent);
  color: #fff;
  display: flex;
  flex: 0 0 4.25rem;
  font-size: 1.2rem;
  font-weight: 950;
  height: 4.25rem;
  justify-content: center;
  width: 4.25rem;
}

.profile-settings__identity {
  min-width: 0;
}

.profile-settings__identity h3 {
  color: var(--hs-text, #0f172a);
  font-size: 1.2rem;
  font-weight: 950;
  line-height: 1.15;
  margin: 0;
}

.profile-settings__identity p {
  color: var(--hs-muted, #64748b);
  font-size: 0.86rem;
  font-weight: 800;
  margin: 0.35rem 0 0;
}

.profile-settings__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.75rem;
}

.profile-settings__form {
  display: grid;
  gap: 1rem;
}

.profile-settings__grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.profile-settings__notice {
  align-items: flex-start;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 16%, transparent);
  border-radius: 18px;
  color: var(--hs-muted, #64748b);
  display: flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 0.65rem;
  line-height: 1.5;
  padding: 0.85rem;
}

@media (max-width: 760px) {
  .profile-settings__summary {
    align-items: flex-start;
  }

  .profile-settings__grid {
    grid-template-columns: 1fr;
  }
}
</style>
